import { useNavigate } from "react-router-dom";
import Image from "../../../../components/Image";
import { useLanguage } from "../../../../context/LanguageContext";
import { Box, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import CheckIcon from "@mui/icons-material/Check";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import { Colors } from "../../../../styles/theme";
import Button from "../../../../components/Button";
import styles from "./Introduction.styles";

const Introduction = () => {
  const { translate, translations, selectedLanguage } = useLanguage();
  const navigate = useNavigate();
  const isRTL = selectedLanguage === "ar";
  const isShortMobile = useMediaQuery(
    "(max-width:600px) and (max-height:750px)"
  );
  const desktopAlignment = isRTL ? "right" : "left";
  const desktopJustification = isRTL ? "flex-end" : "flex-start";
  const assurances = [
    "no credit card required",
    "built for windows",
    "your data under your control",
  ];
  const highlightIcons = {
    "finance-operations": AssessmentOutlinedIcon,
    "local-sql-server-data": StorageOutlinedIcon,
    "multi-company-ready": BusinessOutlinedIcon,
    "preferred-languages": TranslateOutlinedIcon,
  };
  const featureHighlights = translations?.["introduction highlights"] || [];

  const handleExplorePlatform = () => {
    navigate("/#features");
  };

  return (
    <Box
      className="section"
      component="section"
      id="hero"
      style={styles.Introduction_container}
      sx={{
        width: "100%",
        boxSizing: "border-box",
        px: 0,
        justifyContent: { xs: "flex-start", md: "center" },
        height: "auto",
        minHeight: {
          xs: "calc(100vh - 100px)",
          sm: "calc(100vh - 120px)",
          md: "calc(100vh - 140px)",
        },
        overflow: "visible",
        pt: { xs: isShortMobile ? 1.5 : 3, sm: 3, md: 6 },
        pb: { xs: isShortMobile ? 1.5 : 3, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 2fr) minmax(0, 3fr)" },
          gap: { xs: isShortMobile ? 1.5 : 2.5, sm: 3, md: 4 },
          alignItems: "center",
          textAlign: "center",
          width: "100%",
          boxSizing: "border-box",
          height: "auto",
          minHeight: {
            xs: "calc(100vh - 100px)",
            sm: "calc(100vh - 120px)",
            md: "auto",
          },
          alignContent: "center",
          px: { xs: 2, sm: 2.5, md: "44px", xl: "54px" },
        }}
      >
        {/* Text Section */}
        <Box
          style={{
            ...styles.Introduction_textContainer,
          }}
          sx={{
            minWidth: 0,
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <Typography
            variant="subtitle2"
            component="p"
            color={Colors.primary}
            mt={{ xs: 0, md: 4 }}
            mb={{ xs: isShortMobile ? 0.75 : 1.25, sm: 2 }}
            sx={{
              fontWeight: 800,
              letterSpacing: 3,
              textAlign: { xs: "center", md: desktopAlignment },
              textTransform: "uppercase",
            }}
          >
            {translate("introduction eyebrow")}
          </Typography>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontSize: { xs: "2.35rem", md: "4rem" },
              fontWeight: 800,
              letterSpacing: 0,
              lineHeight: 1.08,
              textAlign: { xs: "center", md: desktopAlignment },
            }}
            mt={0}
          >
            <Box component="span" sx={{ color: Colors.navy }}>
              {translate("introduction header1 navy")}
            </Box>{" "}
            <Box component="span" sx={{ color: Colors.primary }}>
              {translate("introduction header1 primary")}
            </Box>
          </Typography>
          <Typography
            variant="h6"
            component="p"
            color={Colors.text}
            mt={{ xs: isShortMobile ? 1 : 1.5, sm: 2 }}
            mb={{ xs: isShortMobile ? 1.5 : 2.5, sm: 4 }}
            sx={{
              fontWeight: 500,
              lineHeight: 1.65,
              textAlign: { xs: "center", md: desktopAlignment },
            }}
          >
            {translate("introduction header2")}
          </Typography>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              width: { xs: "100vw", sm: "100%" },
              mx: { xs: "calc(50% - 50vw)", sm: 0 },
              mb: { xs: isShortMobile ? 1 : 1.5, sm: 2 },
            }}
          >
            <Image
              src={`${process.env.PUBLIC_URL}/images/introduction-1800.webp`}
              srcSet={`${process.env.PUBLIC_URL}/images/introduction-480.webp 480w, ${process.env.PUBLIC_URL}/images/introduction-900.webp 900w, ${process.env.PUBLIC_URL}/images/introduction-1800.webp 1800w`}
              sizes="(max-width: 899px) 100vw, 60vw"
              width={1800}
              height={914}
              fetchpriority="high"
              loading="eager"
              alt={translate("image alt inventory dashboard")}
              sx={{
                ...styles.Introduction_image,
                width: { xs: "100%", sm: "100%" },
                maxWidth: "none",
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: isShortMobile ? 0.5 : 1, sm: 1 },
              flexWrap: "nowrap",
              alignItems: "stretch",
              justifyContent: { xs: "center", md: desktopJustification },
              width: "100%",
              mb: { xs: isShortMobile ? 1 : 1.5, sm: 2 },
            }}
          >
            <Button
              text={translate("start your 14-day trial")}
              styling="primary"
              to="/signup/"
              endIcon={<NorthEastIcon />}
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                margin: 0,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                textAlign: "center",
                lineHeight: 1.2,
                fontSize: {
                  xs: isShortMobile ? "0.9rem" : "1rem",
                  sm: "1.125rem",
                },
                px: { xs: 1.75, sm: 3 },
                py: 1,
              }}
            />
            <Button
              text={translate("explore the platform")}
              styling="secondary"
              handleOnClick={handleExplorePlatform}
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                margin: 0,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                textAlign: "center",
                lineHeight: 1.2,
                fontSize: {
                  xs: isShortMobile ? "0.9rem" : "1rem",
                  sm: "1.125rem",
                },
                px: { xs: 1.75, sm: 3 },
                py: 1,
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: { xs: "center", md: desktopJustification },
              gap: { xs: isShortMobile ? 0.75 : 1, sm: 2 },
              width: "100%",
            }}
          >
            {assurances.map((assurance) => (
              <Box
                key={assurance}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <CheckIcon
                  sx={{
                    color: Colors.green,
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                />
                <Typography
                  variant="body2"
                  color={Colors.text}
                  sx={{
                    fontSize: {
                      xs: isShortMobile ? "0.7rem" : "0.8rem",
                      sm: "1rem",
                    },
                  }}
                >
                  {translate(assurance)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Image Section */}
        <Box
          sx={{
            minWidth: 0,
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
          }}
        >
          <Image
            src={`${process.env.PUBLIC_URL}/images/introduction-1800.webp`}
            srcSet={`${process.env.PUBLIC_URL}/images/introduction-480.webp 480w, ${process.env.PUBLIC_URL}/images/introduction-900.webp 900w, ${process.env.PUBLIC_URL}/images/introduction-1800.webp 1800w`}
            sizes="(max-width: 899px) 100vw, 60vw"
            width={1800}
            height={914}
            fetchpriority="high"
            loading="eager"
            alt={translate("image alt inventory dashboard")}
            sx={{
              ...styles.Introduction_image,
              height: "auto",
              maxHeight: "none",
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          gap: { xs: 1.25, sm: 1.75, md: 2 },
          width: "100%",
          boxSizing: "border-box",
          maxWidth: "none",
          mx: "auto",
          mt: { xs: 3, sm: 3, md: 4 },
          px: { xs: 2, sm: 2.5, md: "44px", xl: "54px" },
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {featureHighlights.map((feature) => {
          const Icon = highlightIcons[feature.key] || BusinessOutlinedIcon;

          return (
            <Box
              key={feature.key}
              sx={{ display: "flex", minWidth: 0 }}
            >
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  minHeight: { xs: 76, md: 92 },
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1.75, md: 2.25 },
                  px: { xs: 2, sm: 2.25, md: 2.75 },
                  py: { xs: 1, md: 1.25 },
                  border: "1px solid #D7E1EF",
                  borderRadius: "8px",
                  backgroundColor: "#F6F8FE",
                  boxShadow: "0 8px 24px rgba(24, 44, 84, 0.04)",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                <Box
                  sx={{
                    width: "42px",
                    height: "42px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    backgroundColor: "#EAF1FA",
                    borderRadius: "8px",
                  }}
                >
                  <Icon sx={{ color: "#3F67A5", fontSize: "22px" }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    color={Colors.primary}
                    sx={{
                      fontSize: {
                        xs: isShortMobile ? "0.7rem" : "0.8rem",
                        sm: "1rem",
                      },
                      lineHeight: 1.2,
                      mb: 0.65,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color={Colors.text}
                    sx={{
                      fontSize: {
                        xs: isShortMobile ? "0.7rem" : "0.8rem",
                        sm: "1rem",
                      },
                      lineHeight: 1.35,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Introduction;
